import { useCallback } from 'react';
import type { Place, PortBoundary } from '../types/warehouse.types';

interface UseSelectionHandlersProps {
  setSelectedPlaceIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  setSelectedPortNames?: React.Dispatch<React.SetStateAction<Set<string>>>;
}

interface UseSelectionHandlersReturn {
  handlePlaceMultiSelect: (place: Place, ctrlKey: boolean) => void;
  handlePlaceSelect: (place: Place | null) => void;
  handlePortMultiSelect: (port: PortBoundary, ctrlKey: boolean) => void;
  handlePortSelect: (port: PortBoundary | null) => void;
}

/**
 * Hook for handling place and port selection logic
 * Provides handlers for single and multi-select operations
 */
export function useSelectionHandlers({ 
  setSelectedPlaceIds,
  setSelectedPortNames 
}: UseSelectionHandlersProps): UseSelectionHandlersReturn {
  // Handle multi-select from canvas (Ctrl+Click)
  const handlePlaceMultiSelect = useCallback((place: Place, ctrlKey: boolean) => {
    setSelectedPlaceIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(place.place_id)) {
        // Already selected, remove it (toggle off)
        newSet.delete(place.place_id);
      } else {
        // Add to selection
        newSet.add(place.place_id);
      }
      return newSet;
    });
  }, [setSelectedPlaceIds]);

  // Handle single place selection (clears multi-selection)
  const handlePlaceSelect = useCallback((place: Place | null) => {
    if (place) {
      setSelectedPlaceIds(new Set([place.place_id]));
    } else {
      setSelectedPlaceIds(new Set());
    }
  }, [setSelectedPlaceIds]);

  // Handle multi-select for ports (Ctrl+Click)
  const handlePortMultiSelect = useCallback((port: PortBoundary, ctrlKey: boolean) => {
    if (!setSelectedPortNames) return;
    
    setSelectedPortNames(prev => {
      const newSet = new Set(prev);
      if (newSet.has(port.name)) {
        // Already selected, remove it (toggle off)
        newSet.delete(port.name);
      } else {
        // Add to selection
        newSet.add(port.name);
      }
      return newSet;
    });
  }, [setSelectedPortNames]);

  // Handle single port selection (clears multi-selection)
  const handlePortSelect = useCallback((port: PortBoundary | null) => {
    if (!setSelectedPortNames) return;
    
    if (port) {
      setSelectedPortNames(new Set([port.name]));
    } else {
      setSelectedPortNames(new Set());
    }
  }, [setSelectedPortNames]);

  return {
    handlePlaceMultiSelect,
    handlePlaceSelect,
    handlePortMultiSelect,
    handlePortSelect
  };
}